import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePermissions } from '@/hooks/usePermissions';
import { InfoTooltip } from '@/components/tours/InfoTooltip';
import { 
  DEMO_CRITICAL_ALERTS, 
  DEMO_ALERT_THRESHOLDS 
} from '@/data/demo-data';
import { format } from 'date-fns';
import { 
  AlertTriangle, 
  CheckCircle2, 
  Bell,
  TrendingDown,
  FileWarning,
  AlertCircle
} from 'lucide-react';

export default function CriticalAlertsCard() {
  const { hasPermission } = usePermissions();

  if (!hasPermission('view_critical_alerts_card')) {
    return null;
  }

  // Check if there are any active critical alerts
  const activeAlerts = DEMO_CRITICAL_ALERTS.filter(a => a.severity !== 'resolved');
  const hasActiveAlerts = activeAlerts.length > 0;

  // Get resolved alerts (last 30 days)
  const resolvedAlerts = DEMO_CRITICAL_ALERTS.filter(a => a.severity === 'resolved');

  return (
    <Card>
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="text-lg font-semibold text-neutral-900">Critical Alerts</span>
          <InfoTooltip
            title="Critical Alerts"
            content="Monitors the group against four key thresholds: minimum cash levels, covenant compliance, scheduled payment deadlines, and forecast variance. You will receive email notifications if any threshold is breached."
          />
        </div>
      </div>
      <CardContent className="space-y-6">
        {/* Current Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Current Status</h3>
            {!hasActiveAlerts ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                No Critical Alerts
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {activeAlerts.length} Active Alert{activeAlerts.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          
          {!hasActiveAlerts && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>System Status: All entities operational</span>
            </div>
          )}
        </div>

        {/* Monitoring Thresholds */}
        <div className="space-y-3 pt-3 border-t">
          <h3 className="font-semibold text-sm">Monitoring Thresholds</h3>
          <div className="space-y-2">
            {DEMO_ALERT_THRESHOLDS.map(threshold => (
              <div key={threshold.id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  {threshold.id === 'threshold-cash' && <TrendingDown className="h-4 w-4 text-muted-foreground" />}
                  {threshold.id === 'threshold-covenant' && <FileWarning className="h-4 w-4 text-muted-foreground" />}
                  {threshold.id === 'threshold-statutory' && <AlertCircle className="h-4 w-4 text-muted-foreground" />}
                  {threshold.id === 'threshold-variance' && <AlertTriangle className="h-4 w-4 text-muted-foreground" />}
                  <span className="text-muted-foreground">{threshold.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {threshold.status === 'clear' ? (
                    <>
                      <span className="text-xs font-medium text-green-700">All clear</span>
                      <div className="w-2 h-2 rounded-full bg-green-600" />
                    </>
                  ) : threshold.status === 'warning' ? (
                    <>
                      <span className="text-xs font-medium text-amber-700">Warning</span>
                      <div className="w-2 h-2 rounded-full bg-amber-600" />
                    </>
                  ) : (
                    <>
                      <span className="text-xs font-medium text-red-700">Critical</span>
                      <div className="w-2 h-2 rounded-full bg-red-600" />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Alerts (Last 30 Days) */}
        {resolvedAlerts.length > 0 && (
          <div className="space-y-3 pt-3 border-t">
            <h3 className="font-semibold text-sm">Recent Alerts (Last 30 Days)</h3>
            <div className="space-y-3">
              {resolvedAlerts.map(alert => (
                <div key={alert.id} className="space-y-2 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-muted-foreground">
                          {format(alert.date, 'MMM d, yyyy')}
                        </span>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          Resolved
                        </Badge>
                      </div>
                      <p className="text-sm font-medium">{alert.message}</p>
                      {alert.entity && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Entity: {alert.entity}
                        </p>
                      )}
                    </div>
                  </div>
                  {alert.resolution && (
                    <div className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground">{alert.resolution}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alert Configuration */}
        <div className="space-y-3 pt-3 border-t">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Alert Configuration
          </h3>
          <div className="text-sm text-muted-foreground space-y-1">
            <p className="font-medium text-foreground mb-2">You receive email notifications for:</p>
            <ul className="space-y-1 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground">•</span>
                <span>Any entity below £50K cash</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground">•</span>
                <span>Scheduled payment overdue</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground">•</span>
                <span>Covenant breach</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground">•</span>
                <span>Variance &gt;15%</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground">•</span>
                <span>Material audit findings</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
